import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";

import { PageShell } from "../components/PageShell.js";
import { Seo } from "../components/Seo.js";

export function About() {
  const { hash } = useLocation();

  useEffect(() => {
    if (!hash) {
      window.scrollTo(0, 0);
      return;
    }
    document
      .getElementById(hash.slice(1))
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [hash]);

  return (
    <PageShell>
      <Seo
        title="About · JivaSeva"
        description="Learn about JivaSeva, an independent, volunteer-driven, not-for-profit initiative inspired by the principle - Life Takes Care of Life. Discover our purpose, our approach, and how we help transform compassion into meaningful action."
        path="/about"
      />

       <div className="page-hero">
        <h1 className="page-hero-name">About JivaSeva</h1>
        <p className="page-hero-tagline">Life Takes Care of Life</p>
      </div>
      <section id="about" className="about-section">
        <p className="manifesto">
          <span className="brand-name">JivaSeva</span> is an independent,
          volunteer-driven, not-for-profit initiative founded on the guiding
          principle that <em>Life Takes Care of Life</em>.
        </p>
        <p className="manifesto">
          Inspired by the understanding that all life, though seemingly
          separate, is an expression of a single underlying reality, we believe
          that the well-being of each depends upon the well-being of all. The
          world's resources are to be shared responsibly and compassionately so
          that all beings may flourish.
        </p>
        <p className="callout">
          💡 When one hand is injured, the other instinctively reaches out to
          comfort it—not because it expects anything in return, but because both
          belong to the same body.
        </p>
        <p className="manifesto">
          Likewise, if all beings are expressions of the same underlying
          reality, caring for another is ultimately an act of caring for
          oneself. That is the spirit of JivaSeva.
        </p>
        <p className="manifesto">
          👉 Read more about{" "}
          <Link to="/spirit" className="inline-link">
            the spirit of JivaSeva
          </Link>
          .
        </p>
      </section>

      <section id="compassion" className="about-section">
        <h2 className="section-heading">Putting Compassion into Action</h2>
        <p className="manifesto">
          Our mission is to remove the practical barriers that often stand in
          the way of compassionate action. We provide tools, resources,
          opportunities, and community support that help make the natural
          impulse to care for one another simpler, more accessible, and more
          meaningful.
        </p>
        <p className="manifesto">
          This philosophy comes alive through practical action. JivaSeva brings
          people together, develops helpful resources, organizes community
          initiatives, establishes volunteer-operated service centers, and,
          where appropriate, creates digital tools that make acts of service
          simpler and more accessible. Depending on the need, JivaSeva may:
        </p>
        <ul className="bullet-list">
          <li>Establish volunteer-operated service stalls and kiosks.</li>
          <li>Organize volunteer and community service initiatives.</li>
          <li>
            Publish educational resources that encourage compassionate action.
          </li>
          <li>Develop digital tools that simplify acts of service.</li>
          <li>
            Create new pathways for individuals and organizations to serve
            others.
          </li>
        </ul>
        <p className="callout">
          💡 Small acts of service, when multiplied across a community, have the
          power to transform lives.
        </p>
      </section>

      <section id="community" className="about-section">
        <h2 className="section-heading">Built by the Community</h2>
        <p className="manifesto">
          Everything within <span className="brand-name">JivaSeva</span> is
          created, built, and supported by volunteers who generously contribute
          their time, skills, creativity, and experience.
        </p>
        <p className="manifesto">
          We welcome everyone who wishes to contribute. Whether you can
          volunteer your time, share professional expertise, offer new ideas,
          support projects, or simply help spread the word, every contribution
          strengthens our collective ability to serve.
        </p>
        <p className="manifesto">
          We envision JivaSeva as an open, collaborative initiative that is
          shaped by its community. As new needs emerge, JivaSeva will continue to
          evolve while remaining grounded in its guiding principle:{" "}
          <em>Life Takes Care of Life</em>.
        </p>
      </section>

      <section id="join" className="about-section">
        <h2 className="section-heading">Join Us</h2>
        <p className="manifesto">
          If this vision resonates with you, we'd love to hear from you.
        </p>
        <p className="manifesto">
          👉 Visit our{" "}
          <Link to="/contact" className="inline-link">
            Contact Page
          </Link>{" "}
          to volunteer, collaborate, share ideas, or learn more about becoming
          part of JivaSeva.
        </p>
      </section>
    </PageShell>
  );
}
